import { Back, Power0, Power1, TweenMax } from 'gsap';
import { GameAudio } from '../../../Config/Audio';
import { getGameSize, IGameSizeInfo } from '../../../Config/Levels';
import Core from '../../../Core/Core';
import getLogger, { ILogger } from '../../../Core/Logger';
import { gameConfig } from '../../../index';
import SoundManager from '../../../Sound/SoundManager';
import { getLevelReward, IRewardItem } from '../../../State/reducers/score';
import { waitForMs } from '../../../Utils/AsyncUtils';
import { createOutlineText, getRandom } from '../../../Utils/Utils';
import { firework } from '../../Animations/Animations';
import { FlavorText } from '../../Animations/FlavorText';
import { SkippableAnimation } from '../../Animations/SkippableAnimation';
import { TextButton } from '../../Shared/Buttons/TextButton';
import { RewardBanner } from './RewardBanner';
import { RewardBox } from './RewardBox';
import { HudActions, HudEmitter } from '../../../Managers/Hud/HudEmitter';
import { LevelActions, LevelEmitter } from '../../../Managers/Level/LevelEmitter';

const logger: ILogger = getLogger('[WIN-POPUP]:');

export class WinLevelPopup extends PIXI.Container {
    private bg: PIXI.Sprite;
    private highlight: PIXI.Sprite;
    private winText: PIXI.Text;
    private char: PIXI.Sprite;
    private banner: RewardBanner;
    private nextButton: TextButton;
    private fwStartTime: number;
    private winScale: number;
    private sfx: FlavorText;
    private replayedLevel: boolean;
    private tweenInAnimation: SkippableAnimation;
    private collectableInProgress: string;
    private collectableProgress: number;
    private rewardAction: (rewards: IRewardItem[]) => Promise<void>;

    constructor(
        rewards: IRewardItem[],
        score: number,
        replay: boolean = false,
        collectableInProgress: string,
        collectableProgress: number
    ) {
        super();

        this.replayedLevel = replay;
        this.collectableInProgress = collectableInProgress;
        this.collectableProgress = collectableProgress;
        const gameSize: IGameSizeInfo = getGameSize();

        const bg = new PIXI.Sprite(PIXI.utils.TextureCache['loading/win-bg']);
        let bScale = gameSize.gameWidth / bg.width;
        if (bg.height * bScale < gameSize.gameHeight) {
            bScale = gameSize.gameHeight / bg.height;
        }
        bg.scale.set(bScale);
        bg.x = (gameSize.gameWidth - bg.width) / 2;
        bg.y = (gameSize.gameHeight - bg.height) / 2;
        bg.alpha = 0;
        bg.interactive = true;

        const highlight = new PIXI.Sprite(PIXI.utils.TextureCache['gameui/win-rays']);
        highlight.scale.set(4);
        highlight.anchor.set(0.5);
        highlight.x = gameSize.gameWidth / 2;
        highlight.y = gameSize.gameHeight * 0.4;
        highlight.alpha = 0;

        const char = new PIXI.Sprite(PIXI.utils.TextureCache['character/character-win']);
        char.anchor.set(0.5, 0);
        char.x = gameSize.gameWidth / 2;
        char.y = gameSize.yOffset + gameConfig.game_height * gameSize.gameScale * 0.1;
        char.alpha = 0;

        const banner = new RewardBanner(rewards, score, collectableInProgress, collectableProgress);
        banner.x = gameSize.gameWidth * 0.5;
        banner.y = char.y + char.height - banner.height / 2;

        const winText = createOutlineText(
            Core.local.getText('won'),
            new PIXI.Rectangle(
                gameSize.gameWidth * 0.5,
                banner.y,
                gameConfig.game_width,
                gameConfig.game_height
            ),
            'Baloo',
            60,
            60
        );
        winText.style = { ...winText.style, ...Core.gameCache.jsons.styleWinText };
        this.winScale = winText.scale.x;
        winText.scale.set(0);
        winText.alpha = 0;
        const sfx = new FlavorText(1);
        sfx.sprite.anchor.set(0.5);
        sfx.sprite.x = gameSize.gameWidth * 0.5;
        sfx.sprite.y = banner.y;
        sfx.sprite.alpha = 0;

        const continueButton = new TextButton({
            width: gameConfig.game_width * 0.5,
            height: gameConfig.game_height * 0.2,
            btnText: Core.local.getText('next'),
        });
        continueButton.x = gameSize.gameWidth / 2;
        continueButton.y = gameSize.gameHeight * 0.76 + continueButton.height * 1.05;
        continueButton.scale.set(0);
        //@ts-ignore
        this.addChild(bg, highlight, char, banner, continueButton, winText, sfx.sprite);

        if (NODE_ENV === 'editor') {
            const scoreText = createOutlineText(
                `Final score: ${score.toString()}`,
                new PIXI.Rectangle(0, 0, bg.width, bg.height),
                'Baloo',
                60
            );
            this.addChild(scoreText);
            scoreText.x = scoreText.width / 2;
            scoreText.y = scoreText.height / 2;
        }

        this.bg = bg;
        this.char = char;
        this.banner = banner;
        this.highlight = highlight;
        this.winText = winText;
        this.sfx = sfx;
        this.nextButton = continueButton;
        this.nextButton.interactive = false;
        this.nextButton.once('trigger', this.handleContinue.bind(this));
        this.createTweenIn();

        this.rewardAction = async (rewards: IRewardItem[]) => {
            HudEmitter.instance.emit(HudActions.RECEIVE_REWARDS, rewards);

            if (Core.sdk.ads.getRewardedEnabled()) {
                await waitForMs(1000);
                this.addWatch();
            } else {
                TweenMax.to(this.nextButton.scale, 0.5, {
                    x: this.nextButton.originalScale.x,
                    y: this.nextButton.originalScale.y,
                    ease: Back.easeOut,
                    onComplete: () => {
                        this.nextButton.interactive = true;
                    },
                });
            }
        };

        LevelEmitter.instance.once(LevelActions.FINAL_REWARD, this.rewardAction);
    }

    public async tweenIn() {
        return this.tweenInAnimation.play();
    }

    public tweenOut() {
        this.banner.dismissBanner();
        TweenMax.to(this, 1, {
            alpha: 0,
            ease: Power1.easeIn,
            onComplete: () => {
                TweenMax.killTweensOf(this.highlight);
                LevelEmitter.instance.emit(LevelActions.WIN_COMPLETE);
                this.destroy();
            },
        });
    }

    private createTweenIn() {
        const anim = (this.tweenInAnimation = new SkippableAnimation());
        anim.add(() => SoundManager.playSound(GameAudio.WIN_JINGLE));
        // fade in win txt
        anim.addToLast(
            TweenMax.to([this.winText, this.sfx], 0.3, {
                alpha: 1,
                ease: Back.easeOut,
                onStart: () => {
                    this.sfx.play();
                },
            })
        );

        anim.addToLast(
            TweenMax.to([this.winText.scale, this.sfx.sprite.scale], 0.3, {
                x: this.winScale,
                y: this.winScale,
                ease: Back.easeOut,
            })
        );
        // fade out win text after 2 seconds
        anim.add(
            TweenMax.to(this.winText, 0.1, {
                alpha: 0,
                delay: 1.5,
            })
        );

        anim.addToLast(() => this.startFireworks());

        anim.addToLast(() => SoundManager.playSound(GameAudio.WIN_FIREWORKS));
        anim.addToLast(() => SoundManager.playSound(GameAudio.WIN_CHEER));

        anim.add(
            TweenMax.to(this.bg, 0.1, {
                alpha: 1,
                onComplete: () => {
                    // this.bg.cacheAsBitmap = true;
                    SoundManager.playSound(GameAudio.WIN_POPUP);
                },
            })
        );

        anim.addToLast(() =>
            TweenMax.to(this.highlight, 4, {
                rotation: 90 * (Math.PI / 180),
                repeat: -1,
                ease: Power0.easeNone,
            })
        ); // Called as a method because it repeats

        anim.addToLast(TweenMax.to(this.highlight, 0.3, { alpha: 1 }));

        anim.add(() =>
            this.banner
                .openBanner()
                .then(() => {
                    TweenMax.to(this.char, 0.4, {
                        y: this.char.y - this.bg.height * 0.1,
                        ease: Power1.easeIn,
                    });
                    TweenMax.to(this.char, 0.25, {
                        alpha: 0,
                    });

                    HudEmitter.instance.once(HudActions.OPEN_REWARD_BOX, this.rewardAction);

                    this.banner.showBest();
                })
                .catch(err => {
                    logger.debug('banner error opening: ' + err);
                })
        );
        anim.addToLast(TweenMax.to(this.char, 0.3, { alpha: 1 }));
    }

    private addWatch() {
        // pass in rewarded, remove gamesdk ref
        if (!this.replayedLevel && Core.sdk.ads.getRewardedEnabled()) {
            this.banner.removeBest();

            const rewards = getLevelReward(3);
            const watchBox = new RewardBox(
                rewards[rewards.length - 1].val as IRewardItem[],
                'ad',
                this.collectableInProgress,
                this.collectableProgress
            );
            watchBox.scale.set(1.4);
            watchBox.x = getGameSize().gameWidth * 0.5;
            watchBox.y = getGameSize().gameHeight * 0.426;
            const explain = createOutlineText(
                Core.local.getText('watchvideo'),
                new PIXI.Rectangle(
                    watchBox.x,
                    getGameSize().gameHeight * 0.33,
                    getGameSize().gameWidth * 0.9,
                    getGameSize().gameHeight * 0.3
                ),
                'Baloo',
                35,
                35
            );
            //@ts-ignore
            this.addChild(watchBox, explain);

            watchBox.initBox(true);

            LevelEmitter.instance.once(LevelActions.WATCH_PRESENT_AD, () => {
                this.nextButton.interactive = false;
                TweenMax.to([explain, this.nextButton], 0.2, {
                    alpha: 0,
                });
            });

            LevelEmitter.instance.once(LevelActions.PRESENT_AD_COMPLETE, () => {
                TweenMax.to(this.nextButton, 0.2, {
                    alpha: 1,
                    delay: 1,
                    onComplete: () => {
                        this.nextButton.interactive = true;
                    },
                });
            });
        }

        TweenMax.to(this.nextButton.scale, 0.5, {
            x: this.nextButton.originalScale.x,
            y: this.nextButton.originalScale.y,
            ease: Back.easeOut,
            onStart: () => {
                this.nextButton.interactive = true;
            },
        });
    }

    private handleContinue(e: PIXI.interaction.InteractionEvent) {
        this.tweenOut();
    }

    private startFireworks() {
        this.fwStartTime = Date.now();
        this.addFirework();
    }

    private addFirework() {
        const gameSize = getGameSize();
        const n = Date.now();

        if (n - this.fwStartTime < 3000) {
            const xDir = Math.round(Math.random()) * 2 - 1;
            const yDir = Math.round(Math.random()) * 2 - 1;

            firework(
                [75, 75],
                [
                    gameSize.gameWidth * 0.5 + getRandom(100, 175) * xDir,
                    gameSize.gameHeight * 0.5 + getRandom(200, 350) * yDir,
                ],
                this
            );

            setTimeout(() => {
                this.addFirework();
            }, getRandom(300, 400));
        }
    }

    public resize(sizeInfo: IGameSizeInfo) {
        let bScale = sizeInfo.gameWidth / this.bg.width;
        if (this.bg.height * bScale < sizeInfo.gameHeight) {
            bScale = sizeInfo.gameHeight / this.bg.height;
        }
        this.bg.scale.set(bScale);
        this.bg.x = (sizeInfo.gameWidth - this.bg.width) / 2;
        this.bg.y = (sizeInfo.gameHeight - this.bg.height) / 2;
        if (this.char) {
            this.char.x = sizeInfo.gameWidth / 2;
        }
        if (this.banner) {
            this.banner.x = sizeInfo.gameWidth / 2;
        }
        if (this.highlight) {
            this.highlight.x = sizeInfo.gameWidth / 2;
        }
        if (this.winText) {
            this.winText.x = sizeInfo.gameWidth / 2;
        }
        if (this.winText) {
            this.winText.x = sizeInfo.gameWidth / 2;
        }
        if (this.nextButton) {
            this.nextButton.x = sizeInfo.gameWidth / 2;
        }
    }
}
